/**
 * Presentation styles for the YAML-driven RAS login page.
 * Keep content/schema decisions in the renderer files; this file only controls
 * responsive layout, typography, spacing, and shared visual treatment.
 */
const styles = (theme) => ({
  Container: {
    background:
      "linear-gradient(147deg, #0E8EB1 0%, #A1D2D5 68%, #E6E6E6 100%), #088CBA",
    fontFamily: 'Lato, "Open Sans", sans-serif',
    padding: "0px 16px 80px",
    boxShadow: "0px 4px 20px 0px #00000040 inset",
    [theme.breakpoints.up("md")]: {
      padding: "0px 32px 50px",
    },
    "@media (min-width: 1400px)": {
      padding: "0px 92px 50px",
    },
  },

  // Hero Section Styles
  HeroSection: {
    padding: "30px 32px 55px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    overflow: "hidden",
    margin: "auto",
  },
  HeroIconWrapper: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: "20px",
    width: "103px",
    height: "103px",
    margin: "0 auto 20px",
  },
  LockBorder: {
    position: "absolute",
    width: "103px",
    height: "103px",
    top: 0,
    left: 0,
  },
  HeroIcon: {
    position: "relative",
    width: "101px",
    height: "101px",
    zIndex: 1,
  },
  HeroTitle: {
    fontFamily: "Inter",
    fontSize: "30px",
    fontWeight: 500,
    color: "#FFFFFF",
    marginTop: "10px",
    lineHeight: "107%",
    letterSpacing: "-2%",
  },

  // Content Wrapper
  ContentWrapper: {
    margin: "auto",
    padding: "0px",
  },
  ContentLoadNotice: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "12px",
    boxSizing: "border-box",
    width: "calc(100% + 32px)",
    maxWidth: "none",
    margin: "0 -16px 32px",
    padding: "10px 56px 10px 24px",
    backgroundColor: "#FFF7E6",
    borderTop: "1px solid #B86B00",
    borderBottom: "1px solid #B86B00",
    color: "#000",
    fontFamily: "Roboto",
    lineHeight: "150%",
    textAlign: "center",
    [theme.breakpoints.up("md")]: {
      width: "calc(100% + 64px)",
      margin: "0 -32px 32px",
    },
    "@media (min-width: 1400px)": {
      width: "calc(100% + 184px)",
      margin: "0 -92px 32px",
    },
  },
  ContentLoadNoticeText: {
    margin: "0",
    fontFamily: "Roboto",
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "150%",
  },
  ContentLoadNoticeDismiss: {
    position: "absolute",
    right: "20px",
    top: "50%",
    transform: "translateY(-50%)",
    border: 0,
    backgroundColor: "transparent",
    color: "#000",
    cursor: "pointer",
    fontFamily: "Roboto",
    fontSize: "22px",
    fontWeight: 700,
    lineHeight: 1,
    padding: "2px 6px",
  },
  ColumnContainer: {
    gap: "60px",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      gap: "40px",
    },
  },

  // Login Section Box Styles
  LoginSectionBox: {
    backgroundColor: "#F0F8FA",
    borderRadius: "35px",
    padding: "30px 40px 50px",
    boxShadow: "0px 2px 10px 0px #00000040",
    border: "3px solid #F4FBFE",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0px",
    "&:not(:first-child)": {
      marginTop: "40px",
    },
  },
  AccordionList: {
    alignSelf: "stretch",
    width: "100%",
    borderTop: "1px solid #8A8A8A",
  },
  AccordionItem: {
    width: "100%",
    borderBottom: "1px solid #8A8A8A",
  },
  AccordionHeader: {
    minHeight: "52px",
    padding: "14px 10px 14px 0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
  },
  AccordionTitle: {
    flex: 1,
    fontFamily: "Inter",
    fontSize: "20px",
    fontWeight: 600,
    color: "#000",
    lineHeight: "26px",
  },
  AccordionText: {
    width: "100%",
    fontFamily: "Roboto",
    fontSize: "16px",
    fontWeight: 400,
    color: "#000",
    lineHeight: "150%",
    padding: "0 50px 18px 0",
    [theme.breakpoints.down("sm")]: {
      paddingRight: 25,
    },
  },
  RightColumn: {
    [theme.breakpoints.up("md")]: {
      maxWidth: "329px",
    },
    // marginLeft: "60px",
  },
  LeftColumn: {
    [theme.breakpoints.up("md")]: {
      maxWidth: "876px",
    },
  },
  SectionTextWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "10px",
    minWidth: 0,
  },
  orderedListAlpha: {
    margin: "2px 0",
    paddingLeft: "48px",
    listStyleType: "lower-alpha",
    fontFamily: "Roboto",
    fontWeight: 400,
    fontSize: "16px",
    color: "#000",
    letterSpacing: "0.2px",
    lineHeight: "150%",
    "& li": {
      marginBottom: "2px",
      paddingLeft: "4px",
    },
  },
  orderedListNumeric: {
    margin: "2px 0",
    paddingLeft: "25px",
    fontFamily: "Roboto",
    fontSize: "16px",
    color: "#000",
    letterSpacing: "0.2px",
    lineHeight: "150%",
    "& li": {
      marginBottom: "2px",
      paddingLeft: "4px",
    },
  },
  unorderedList: {
    margin: "2px 0",
    paddingLeft: "25px",
    fontFamily: "Roboto",
    fontSize: "16px",
    color: "#000",
    letterSpacing: "0.2px",
    lineHeight: "150%",
    "& li": {
      marginBottom: "2px",
      paddingLeft: "4px",
    },
  },
  nestedListOnlyItem: {
    listStyleType: "none",
    paddingLeft: "0px !important",
    marginBottom: "0px !important",
  },
  nestedList: {
    margin: "2px 0",
    paddingLeft: "20px",
    listStyleType: "disc",
    "& li": {
      marginBottom: "2px",
      paddingLeft: "4px",
    },
  },
  SectionContentRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "44px",
    paddingTop: "0px",
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      gap: "25px",
    },
  },
  LoginButtonContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      alignSelf: "center",
    },
  },
  BodyText: {
    fontFamily: "Roboto",
    fontSize: "16px",
    fontWeight: 400,
    color: "#000",
    letterSpacing: "0.2px",
    lineHeight: "150%",
    marginBottom: "0px",
  },
  inlineText: {
    display: "inline",
  },
  MarkdownContent: {
    width: "100%",
    "& p": {
      marginTop: 0,
    },
    "& p:not(:last-child)": {
      marginBottom: "0px",
    },
    "& li p": {
      display: "inline",
      margin: 0,
    },
    "& li ul, & li ol": {
      marginTop: "2px",
    },
  },
  LoginButtonRas: {
    minWidth: "215px",
    minHeight: "47px",
    fontFamily: "Roboto",
    fontSize: "16px",
    fontWeight: 500,
    color: "#000000",
    backgroundColor: "#FFFFFF",
    border: "1.25px solid #3795A9",
    borderRadius: "10px",
    padding: "11px 53px",
    gap: "10px",
    textTransform: "none",
    textAlign: "center",
    lineHeight: "24.5px",
    whiteSpace: "normal",
    overflowWrap: "break-word",

    letterSpacing: "0%",

    "&:hover": {
      backgroundColor: "#F0F9F7",
      border: "1.25px solid #3795A9",
    },
    "&.Mui-disabled": {
      color: "#5F6368",
      backgroundColor: "#F5F5F5",
      border: "1.25px solid #9AA0A6",
    },
  },
  LoginButtonAlert: {
    maxWidth: "260px",
    marginTop: "10px",
    fontFamily: "Roboto",
    fontSize: "14px",
    fontWeight: 500,
    color: "#8A1C1C",
    lineHeight: "18px",
    textAlign: "center",
  },

  // Help Sidebar Styles
  HelpSidebar: {
    backgroundColor: "#004D73",
    borderRadius: "35px",
    padding: "20px 30px 50px 30px",
    color: "#FFFFFF",
    boxShadow: "0px 2px 10px 0px #00000040",
  },
  HelpHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: "15px",
    gap: "15px",
  },
  HelpIcon: {
    width: "52px",
    height: "52px",
  },
  HelpHeaderText: {
    fontFamily: "Nunito",
    fontSize: "18px",
    fontWeight: 600,
    color: "#FFFFFF",
    lineHeight: "107%",
    letterSpacing: "5%",
    textTransform: "uppercase",
  },
  TutorialSection: {
    marginBottom: "60px",
    padding: "0px 10px",
  },
  HelpContentSection: {
    padding: "0px 10px",
  },
  SidebarTitle: {
    fontFamily: "Inter",
    fontSize: "24px",
    fontWeight: 700,
    color: "#8BEAFB",
    marginBottom: "20px",
    lineHeight: "112%",
    letterSpacing: "-2%",
  },
  SidebarText: {
    fontFamily: "Lato",
    fontSize: "16px",
    fontWeight: 400,
    color: "white",
    lineHeight: "21px",
    letterSpacing: "0.2px",
  },
  VideoThumbnail: {
    marginTop: "25px",
    cursor: "pointer",
    position: "relative",
    overflow: "hidden",
  },
  VideoImage: {
    width: "100%",
    height: "auto",
    display: "block",
  },
  PlayOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "8px",
  },
  PlayIcon: {
    width: "23%",
    height: "auto",
  },
  ContactSection: {
    padding: "0px 10px",
  },
  ContactButton: {
    fontFamily: "Roboto",
    fontSize: "16px",
    fontWeight: 500,
    color: "#000",
    backgroundColor: "#FFF",
    border: "1.25px solid #3795A9",
    borderRadius: "10px",
    padding: "11px 53px",
    textTransform: "none",
    width: "100%",
    lineHeight: "24.5px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.9)",
      border: "1.25px solid #3795A9",
    },
    maxWidth: "213px",
    height: "47px",
    display: "block",
    margin: "43px auto 0",
  },

  LoginSectionHeader: {
    alignSelf: "stretch",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "15px",
  },
  LoginSectionBody: {
    alignSelf: "stretch",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "15px",
  },
  SectionTitle: {
    fontFamily: "Inter",
    fontSize: "24px",
    fontWeight: 700,
    color: "#18588E",
    lineHeight: "25.57px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    paddingBottom: "15px",
  },
  SubsectionTitle: {
    fontFamily: "Inter",
    fontSize: "20px",
    fontWeight: 600,
    color: "black",
    marginTop: "17px",
    marginBottom: "17px",
    lineHeight: "130%",
    letterSpacing: "0%",
  },
  title: {
    color: "inherit",
    fontFamily: "Roboto",
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: "24px",
  },
  space: {
    display: "block",
    height: "15px",
  },
  head: {
    display: "block",
    color: "#18588E",
    fontFamily: "Inter",
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: "150%",
    margin: "14px 0 4px",
  },
  firstTitle: {
    display: "block",
    color: "#18588E",
    fontFamily: "Inter",
    fontSize: "20px",
    fontWeight: 600,
    lineHeight: "150%",
    margin: "0 0 4px",
  },
  italicizeText: {
    display: "inline-block",
    marginLeft: "30px",
    fontStyle: "italic",
  },
  email: {
    color: "#18588E",
    fontWeight: 700,
  },
  indentedText: {
    display: "inline-block",
    marginLeft: "30px",
  },
  tableDiv: {
    width: "100%",
    overflowX: "auto",
    margin: "8px 0",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontFamily: "Roboto",
    fontSize: "14px",
    lineHeight: "150%",
  },
  tableHeader: {
    backgroundColor: "#EAF3F8",
  },
  tableBodyRow: {
    borderBottom: "1px solid #8A8A8A",
  },
  headerCell: {
    padding: "8px",
    textAlign: "left",
    fontWeight: 700,
    color: "#18588E",
    border: "1px solid #8A8A8A",
  },
  tableCell: {
    padding: "8px",
    verticalAlign: "top",
    border: "1px solid #8A8A8A",
  },
  Link: {
    color: "#18588E",
    textDecoration: "underline",
    fontWeight: 400,
    cursor: "pointer",
    letterSpacing: "0.2px",
    lineHeight: "150%",
    "&:hover": {
      textDecoration: "underline",
    },
    "& a": {
      color: "inherit",
    },
    "& a:visited": {
      color: "inherit",
    },
    fontFamily: "Roboto",
    fontSize: "16px",
    "& $unorderedList": {
      color: "#18588E",
    },
  },
  linkIcon: {
    display: "inline-block",
    width: "17px",
    height: "17px",
    marginLeft: "4px",
    verticalAlign: "middle",
    backgroundColor: "#18588E",
  },

  // Warning Section
  WarningSection: {
    width: "100%",
    padding: "30px 40px",
    marginTop: "40px",
    backgroundColor: "#F4FBFE",
    borderRadius: "35px",
    boxShadow: "0px 2px 10px 0px #00000040",
    border: "3px #BA1F40 solid",
    display: "flex",
    gap: "40px",
  },
  WarningContent: {
    flex: "1 1 0",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "15px",
  },
  WarningToggle: {
    alignSelf: "stretch",
    width: "100%",
    boxSizing: "border-box",
    paddingRight: "10px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
  },
  WarningToggleStatic: {
    cursor: "default",
  },
  WarningTitle: {
    fontFamily: "Inter",
    fontSize: "20px",
    fontWeight: 600,
    color: "#BA1F40",
    lineHeight: "130%",
    letterSpacing: "0%",
  },
  WarningText: {
    alignSelf: "stretch",
    width: "100%",
    boxSizing: "border-box",
    flex: 1,
    fontFamily: "Roboto",
    fontSize: "16px",
    fontWeight: 400,
    color: "black",
    lineHeight: "24px",
    letterSpacing: "0.2px",
    paddingRight: "30px",
  },
  WarningTextCollapsed: {
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 1,
    WebkitBoxOrient: "vertical",
    textOverflow: "ellipsis",
    marginRight: "10px",
    wordSpacing: "3px",
  },
});

export default styles;
